/**
 * reconcileOrders.js — keep unchanged live orders as the SAME objects across polls.
 *
 * /api/square-tickets answers every poll with a freshly parsed list, so every
 * order was a new object every 10 s even when nothing had changed. The board
 * treated that as "everything changed": it re-merged every ticket, re-rendered
 * every card and rewrote the whole list to localStorage on each poll.
 *
 * Here an incoming order that is identical to the one already held keeps the
 * held object, and when nothing at all changed the held ARRAY is returned — so
 * a quiet poll changes nothing downstream, and a busy one only touches the
 * orders that actually moved.
 */

// Each order's serialized form, computed once per object.
const sigCache = new WeakMap();
function sig(o) {
  let s = sigCache.get(o);
  if (s === undefined) {
    s = JSON.stringify(o);
    sigCache.set(o, s);
  }
  return s;
}

/**
 * @param {object[]} prev — the orders currently held
 * @param {object[]} next — the orders just fetched
 * @returns {object[]} `prev` itself when nothing changed; otherwise `next`, with
 *   every unchanged order swapped for its `prev` object.
 */
export function reconcileOrders(prev, next) {
  if (!Array.isArray(prev) || prev.length === 0) return next;
  const held = new Map();
  prev.forEach(o => { if (o && o.id != null) held.set(o.id, o); });
  let same = prev.length === next.length;
  const out = next.map((o, i) => {
    const old = o && o.id != null ? held.get(o.id) : undefined;
    const keep = old && sig(old) === sig(o) ? old : o;
    if (keep !== prev[i]) same = false;
    return keep;
  });
  return same ? prev : out;
}
